import { useMemo, useState } from "react"
import { Box, useTheme } from "@mui/material"
import { Grid, Typography } from "src/lib"
import { niceTicks } from "src/lib/utils/chartMath"
import { formatAxisDate, formatTooltipDate } from "src/lib/utils/periodRange"

interface MultiSeriesTrendChartSeries {
    id: string
    label: string
    color: string
    points: { date: string; value: number }[]
    /** Estilo de referência (ex.: a média), não uma entidade competindo no ranking. */
    dashed?: boolean
}

interface MultiSeriesTrendChartProps {
    series: MultiSeriesTrendChartSeries[]
    format: (value: number) => string
}

const CHART_W = 1000
const CHART_H = 260
const PADDING = { top: 20, right: 16, bottom: 28, left: 40 }

/**
 * Gráfico de múltiplas séries alinhadas pelo mesmo eixo de datas (ex.: a curva média
 * de execução + até 3 assessores selecionados). Sempre com legenda (traço colorido)
 * e um único tooltip por posição do cursor, listando o valor de cada série naquele ponto.
 */
const MultiSeriesTrendChart = ({ series, format }: MultiSeriesTrendChartProps) => {
    const theme = useTheme() as any
    const [hoverIndex, setHoverIndex] = useState<number | null>(null)

    const plotW = CHART_W - PADDING.left - PADDING.right
    const plotH = CHART_H - PADDING.top - PADDING.bottom
    const pointCount = Math.max(...series.map((s) => s.points.length), 1)

    const { ticks, yMin, yMax } = useMemo(() => {
        const allValues = series.flatMap((s) => s.points.map((p) => p.value))
        const t = niceTicks(Math.min(...allValues), Math.max(...allValues), 4)
        return { ticks: t, yMin: t[0], yMax: t[t.length - 1] }
    }, [series])

    const xAt = (i: number) => PADDING.left + (pointCount <= 1 ? plotW / 2 : (i / (pointCount - 1)) * plotW)
    const yAt = (v: number) => PADDING.top + plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH

    const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
        const rect = e.currentTarget.getBoundingClientRect()
        const relX = ((e.clientX - rect.left) / rect.width) * CHART_W
        let nearest = 0
        let best = Infinity
        for (let i = 0; i < pointCount; i++) {
            const d = Math.abs(xAt(i) - relX)
            if (d < best) {
                best = d
                nearest = i
            }
        }
        setHoverIndex(nearest)
    }

    const handleKeyDown = (e: React.KeyboardEvent<SVGSVGElement>) => {
        if (e.key === "ArrowRight") {
            e.preventDefault()
            setHoverIndex((i) => Math.min(pointCount - 1, (i ?? -1) + 1))
        } else if (e.key === "ArrowLeft") {
            e.preventDefault()
            setHoverIndex((i) => Math.max(0, (i ?? pointCount) - 1))
        } else if (e.key === "Escape") {
            setHoverIndex(null)
        }
    }

    const lastIndex = pointCount - 1
    const axisLabelIndices = Array.from(new Set([0, Math.floor(lastIndex / 2), lastIndex]))
    const gridColor = "rgba(255, 255, 255, 0.08)"
    const axisTextColor = "rgba(255, 255, 255, 0.45)"
    const referenceDates = series.find((s) => s.points.length === pointCount)?.points ?? series[0]?.points ?? []

    return (
        <Box>
            <Grid container gap={2} sx={{ mb: 1.5, flexWrap: "wrap" }}>
                {series.map((s) => (
                    <Grid key={s.id} container alignItems="center" gap={0.75} sx={{ width: "auto" }}>
                        <Box
                            sx={{
                                width: 16,
                                height: 0,
                                borderTop: `2px ${s.dashed ? "dashed" : "solid"} ${s.color}`,
                            }}
                        />
                        <Typography variant="caption" color="quaternary">
                            {s.label}
                        </Typography>
                    </Grid>
                ))}
            </Grid>
            <Box sx={{ position: "relative", width: "100%" }}>
                <svg
                    viewBox={`0 0 ${CHART_W} ${CHART_H}`}
                    width="100%"
                    height={CHART_H}
                    preserveAspectRatio="none"
                    tabIndex={0}
                    role="img"
                    aria-label="Gráfico com múltiplas séries de performance de execução ao longo do tempo, use as setas para navegar pelos pontos"
                    onPointerMove={handlePointerMove}
                    onPointerLeave={() => setHoverIndex(null)}
                    onKeyDown={handleKeyDown}
                    style={{ outline: "none" }}
                >
                    {ticks.map((t) => (
                        <g key={t}>
                            <line x1={PADDING.left} x2={CHART_W - PADDING.right} y1={yAt(t)} y2={yAt(t)} stroke={gridColor} strokeWidth={1} />
                            <text x={0} y={yAt(t) - 4} fontSize={11} fill={axisTextColor}>
                                {format(t)}
                            </text>
                        </g>
                    ))}

                    {series.map((s) => {
                        const linePath = s.points.map((p, i) => `${i === 0 ? "M" : "L"} ${xAt(i)} ${yAt(p.value)}`).join(" ")
                        const lastPoint = s.points[s.points.length - 1]
                        return (
                            <g key={s.id}>
                                <path
                                    d={linePath}
                                    fill="none"
                                    stroke={s.color}
                                    strokeWidth={2}
                                    strokeDasharray={s.dashed ? "6 4" : undefined}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                {lastPoint && (
                                    <circle
                                        cx={xAt(s.points.length - 1)}
                                        cy={yAt(lastPoint.value)}
                                        r={4}
                                        fill={s.color}
                                        stroke={theme.palette.neutral.darkest}
                                        strokeWidth={2}
                                    />
                                )}
                            </g>
                        )
                    })}

                    {axisLabelIndices.map((i) => (
                        <text key={i} x={xAt(i)} y={CHART_H - 8} fontSize={11} fill={axisTextColor} textAnchor="middle">
                            {referenceDates[i] ? formatAxisDate(referenceDates[i].date) : ""}
                        </text>
                    ))}

                    {hoverIndex !== null && (
                        <line
                            x1={xAt(hoverIndex)}
                            x2={xAt(hoverIndex)}
                            y1={PADDING.top}
                            y2={PADDING.top + plotH}
                            stroke="rgba(255, 255, 255, 0.25)"
                            strokeWidth={1}
                        />
                    )}
                </svg>
                {hoverIndex !== null && (
                    <Box
                        sx={{
                            position: "absolute",
                            left: `${(xAt(hoverIndex) / CHART_W) * 100}%`,
                            top: 8,
                            transform: "translateX(-50%)",
                            bgcolor: "neutral.darker",
                            border: "1px solid rgba(255, 255, 255, 0.08)",
                            borderRadius: 1,
                            px: 1.5,
                            py: 0.75,
                            pointerEvents: "none",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {referenceDates[hoverIndex] && (
                            <Typography variant="caption" color="quaternary" sx={{ display: "block", mb: 0.25 }}>
                                {formatTooltipDate(referenceDates[hoverIndex].date)}
                            </Typography>
                        )}
                        {series.map((s) => {
                            const point = s.points[hoverIndex]
                            if (!point) return null
                            return (
                                <Grid key={s.id} container alignItems="center" gap={0.75} wrap="nowrap">
                                    <Box sx={{ width: 10, height: 0, borderTop: `2px ${s.dashed ? "dashed" : "solid"} ${s.color}` }} />
                                    <Typography variant="caption" color="quaternary" sx={{ flex: 1 }}>
                                        {s.label}
                                    </Typography>
                                    <Typography sx={{ fontWeight: 700, color: "neutral.lighter", fontSize: 13 }}>
                                        {format(point.value)}
                                    </Typography>
                                </Grid>
                            )
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export { MultiSeriesTrendChart }
export type { MultiSeriesTrendChartSeries }
